import {
    Button,
    Flex,
    HelpTip,
    Icon,
    Link,
    Text,
    Heading,
} from "@amzn/storm-ui";
import { infoCircle } from "@amzn/storm-ui-icons";
import React, { useEffect } from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import { authStore } from "../../store/auth";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { getStateKeys } from "../../store/zustand.helpers";
import { APPENV } from "../../utils/app.environment";
import { isTestSiteOrDev } from "../../utils/environment.utils";
import {
    QueryParam,
    getAdvertiserId,
    getMarketplaceId,
} from "../../utils/permission.utils";
import {
    sfAccountToIdentifier,
    sfIdentifierToAccount,
} from "../../utils/testSite.utils";

import { AccountPicker } from "./AccountPicker";
import { FeatureFlagPicker } from "./FeatureFlagPicker";
import { SpoofAsUserInput } from "./SpoofAsUserInput";
import { TEST_SITE_NAME } from "./common";

const ToolbarContainer = styled.div`
    padding: 0px 24px;
`;

const PickerContainer = styled(Flex)`
    align-items: end;
`;

const TitleText = styled(Heading)`
    padding: 0px 0px 5px 5px;
`;

const Picker = styled.div<{ width: number }>`
    padding: 5px;
    width: ${(props) => props.width.toString()};
`;

export const TestSiteToolbar = (): JSX.Element | null => {
    const [featureFlags, setFeatureFlags] = React.useState<string[]>([]);
    const [username] = useStore(authStore, (state) => [state.username]);
    const [sfAccountIdentifier, setSfAccountIdentifier] =
        React.useState<string>(
            sfAccountToIdentifier({
                [QueryParam.SALESFORCE_ID]: getAdvertiserId() ?? "",
                [QueryParam.MARKETPLACE_ID]: getMarketplaceId() ?? "",
            }),
        );

    useEffect(() => {
        const featureFlagsState = FeatureFlagsStore.getState();
        const featureFlagKeys = getStateKeys(featureFlagsState);
        const active: string[] = [];
        featureFlagKeys.forEach((ff) => {
            if (featureFlagsState[ff]) {
                active.push(ff);
            }
        });
        setFeatureFlags(active);
    }, [FeatureFlagsStore.getState()]);

    // Needs to instantiate after setStates and react hooks
    if (!isTestSiteOrDev()) {
        return null;
    }

    const handleConfiguration = () => {
        const searchParams = new URLSearchParams();
        const sfAccount = sfIdentifierToAccount(sfAccountIdentifier);
        sfAccount[QueryParam.SALESFORCE_ID] &&
            searchParams.set(
                QueryParam.SALESFORCE_ID,
                sfAccount[QueryParam.SALESFORCE_ID],
            );
        sfAccount[QueryParam.MARKETPLACE_ID] &&
            searchParams.set(
                QueryParam.MARKETPLACE_ID,
                sfAccount[QueryParam.MARKETPLACE_ID],
            );
        featureFlags.forEach((ff) => {
            searchParams.set(ff, "true");
        });
        window.location.search = searchParams.toString();
    };

    const horizonHelpText = (
        <div>
            This is Horizon, the Insights Hub test site.
            <br />
            If you are trying to access Insights Hub, please visit -
            <br />
            <li>
                <Link href="https://advertising.amazon.com/book-of-business">
                    NA
                </Link>
            </li>
            <li>
                <Link href="https://advertising.amazon.co.uk/book-of-business">
                    EU
                </Link>
            </li>
            <li>
                <Link href="https://advertising.amazon.co.jp/book-of-business">
                    FE Japanese
                </Link>
            </li>
            <li>
                <Link href="https://advertising.amazon.com.au/book-of-business">
                    FE English
                </Link>
            </li>
            Click <strong>View All</strong> under the Insights column to open
            IHub for the account
            <br />
        </div>
    );

    return (
        <ToolbarContainer>
            <Flex direction="row" justifyContent="space-between">
                <Flex direction="row">
                    <TitleText renderAs="h1">{TEST_SITE_NAME}</TitleText>
                    {APPENV.dev_server && (
                        <TitleText renderAs="h5" color="tertiary">
                            dev-server:{APPENV.endpoints_stage}
                        </TitleText>
                    )}
                    <HelpTip
                        position="right"
                        renderTrigger={<Icon type={infoCircle} />}
                    >
                        {horizonHelpText}
                    </HelpTip>
                </Flex>
                <Text size="large">
                    <strong>Logged in as:</strong> {username}
                </Text>
            </Flex>
            <Flex
                direction="row"
                justifyContent="space-between"
                alignItems="end"
            >
                <PickerContainer>
                    <Picker width={200}>
                        <AccountPicker
                            sfAccountIdentifier={sfAccountIdentifier}
                            setSfAccountIdentifier={setSfAccountIdentifier}
                        />
                    </Picker>
                    <Picker width={400}>
                        <FeatureFlagPicker
                            featureFlags={featureFlags}
                            setFeatureFlags={setFeatureFlags}
                        />
                    </Picker>
                    <Picker width={100}>
                        <Button
                            variant="primary"
                            onClick={handleConfiguration}
                            data-testid="use-configuration"
                        >
                            Use Configuration
                        </Button>
                    </Picker>
                </PickerContainer>
                <PickerContainer>
                    <SpoofAsUserInput />
                </PickerContainer>
            </Flex>
        </ToolbarContainer>
    );
};
